import { AfterViewInit, Component, effect, inject, Input, signal, ElementRef } from '@angular/core';
import { ChatService } from '../../../services/chat/chat.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MediaService } from '../../../services/media/media.service';

@Component({
  selector: 'app-create-room-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './create-room-modal.html',
  styleUrl: './create-room-modal.scss',
})
export class CreateRoomModal implements AfterViewInit {
  private client = inject(ChatService);
  private media = inject(MediaService);
  private el = inject(ElementRef);

  public arenaIcons = this.media.arenaIcons;

  @Input()
  public parentGuid: string | null = null;

  public arenaSignal = signal<Arena>({
    guid: '',
    parentGuid: this.parentGuid ?? '',
    title: '',
    motd: '',
    description: '',
    avatarURL: '/images/logo_1_2.png',
    url: '',
    ownerId: '',
    parentOwnerId: '',
    isProtected: false,
    isOfficial: false,
    isPublic: true,
    isStatic: false,
    hasVoice: true,
    hasVideo: false,
    hasImages: true,
    hasWebView: false,
    creationDate: '',
    password: '',
    selectedGame: null,
    games: []
  });

  public arenaForm = new FormGroup({
    guid: new FormControl(this.arenaSignal().guid),
    parentGuid: new FormControl(this.arenaSignal().parentGuid),
    title: new FormControl(this.arenaSignal().title, {
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50)
      ]
    }),
    motd: new FormControl(this.arenaSignal().motd, {
      validators: [
        Validators.minLength(0),
        Validators.maxLength(100)
      ]
    }),
    description: new FormControl(this.arenaSignal().description, {
      validators: [
        Validators.minLength(0),
        Validators.maxLength(200)
      ]
    }),
    avatarURL: new FormControl(this.arenaSignal().avatarURL, {
      validators: [
        Validators.required,
        Validators.minLength(10)
      ]
    }),
    url: new FormControl(this.arenaSignal().url),
    ownerId: new FormControl(this.arenaSignal().ownerId),
    parentOwnerId: new FormControl(this.arenaSignal().parentOwnerId),
    isProtected: new FormControl(this.arenaSignal().isProtected),
    isOfficial: new FormControl(this.arenaSignal().isOfficial),
    isPublic: new FormControl(this.arenaSignal().isPublic),
    isStatic: new FormControl(this.arenaSignal().isStatic),
    hasVoice: new FormControl(this.arenaSignal().hasVoice),
    hasVideo: new FormControl(this.arenaSignal().hasVideo),
    hasImages: new FormControl(this.arenaSignal().hasImages),
    hasWebView: new FormControl(this.arenaSignal().hasWebView),
    creationDate: new FormControl(this.arenaSignal().creationDate),
    password: new FormControl(this.arenaSignal().password ?? ""),
    selectedGame: new FormControl(this.arenaSignal().selectedGame),
    games: new FormControl(this.arenaSignal().games),
  });

  public selectedIcon = signal<any>(null);

  constructor() {}

  /*
   * Initialize media retrieval after view initialization.
   */
  ngAfterViewInit(): void {
    console.log("Create => ", this.parentGuid);
    this.media.getArenaMedia(this.parentGuid ?? '', 2);

    this.arenaForm.valueChanges.subscribe(val => {
      // Cast val to Arena safely
      const arenaVal: Arena = {
        ...val,
        avatarURL: val.avatarURL || '/images/logo_1_2.png',
        isProtected: val.password !== ''
      } as Arena;

      this.arenaSignal.update(() => arenaVal);
    });
  }

  /*
   * Create a new room with current form values.
   */
  public create() {
    this.arenaForm.markAllAsTouched();
    if (!this.arenaForm.controls.avatarURL.value) {
      this.arenaForm.controls.avatarURL.setValue('/images/logo_1_2.png');
    }
    if (this.arenaForm.valid) {
      const arena = {
        ...this.arenaSignal(),
        avatarURL: this.arenaForm.controls.avatarURL.value || '/images/logo_1_2.png'
      };
      this.client.create(arena);

      const closeButton = this.el.nativeElement.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
    } else {
      console.log(this.arenaForm);
    }
  }

  /*
   * Select a custom icon for the room.
   */
  public selectIcon(icon: any) {
    let arena = this.arenaSignal();
    arena.avatarURL = `https://usercontent.h5arena.com/arenas/${icon.guid}${icon.fileExtension}`;
    this.arenaSignal.set(arena);
    this.arenaForm.controls.avatarURL.setValue(arena.avatarURL);
    this.selectedIcon.set(icon);
  }
}

export interface ArenaGame {
  id: string;
  name: string;
}

export interface Arena {
  guid: string;
  parentGuid: string;
  title: string;
  motd: string;
  description: string;
  avatarURL: string;
  url: string;
  ownerId: string;
  parentOwnerId: string;
  isProtected: boolean;
  isOfficial: boolean;
  isPublic: boolean;
  isStatic: boolean;
  hasVoice: boolean;
  hasVideo: boolean;
  hasImages: boolean;
  hasWebView: boolean;
  creationDate: string;
  password: string;
  selectedGame: ArenaGame | null;
  games: ArenaGame[];
}